import { z } from 'zod';

/**
 * A debate format is a plain JSON document so it can be written by hand, by a form, or (later) by AI.
 * Note-taking is identical for every format: segments become flow columns, sheets group them.
 *
 * Slots: 'first' / 'second' = speaking order. Which side (e.g. Pro/Con) holds each slot is decided per round
 * (coin flip in PF, fixed in LD/Policy).
 */
export const SegmentKind = z.enum(['speech', 'questioning']);
export const SegmentRole = z.enum(['constructive', 'rebuttal', 'summary', 'final', 'questioning', 'other']);
export const SlotRef = z.enum(['first', 'second', 'both']);

export const SegmentSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,12}$/, 'Short id must be 1–12 letters, numbers, - or _.'),
  kind: SegmentKind,
  role: SegmentRole,
  label: z.string().trim().min(1).max(40),
  short: z.string().trim().min(1).max(6),
  durationSec: z.number().int().min(10).max(3600),
  by: SlotRef,
  /** 1-based speaker numbers within the team(s). Empty = anyone. */
  speakers: z.array(z.number().int().min(1).max(5)).max(5),
});

export const SheetSchema = z.object({
  id: z.string().min(1).max(24),
  /** "{first}" / "{second}" are replaced by the side label holding that slot. */
  title: z.string().min(1).max(40),
  owner: z.enum(['first', 'second']).nullable(),
  segmentIds: z.array(z.string()).min(1),
});

export const FormatSchema = z
  .object({
    id: z.string().min(1).max(40),
    version: z.literal(1),
    name: z.string().trim().min(1).max(60),
    /** Which event this is (formats are event × rules, e.g. Parli under CHSSA rules). Optional for older snapshots. */
    event: z.enum(['pf', 'ld', 'policy', 'parli']).optional(),
    /** Whose rules the times come from, e.g. "CHSSA" or "NSDA". */
    ruleset: z.string().trim().max(20).optional(),
    /** Parli: points of information aren't allowed in the first or last this-many seconds of a constructive. */
    poiProtectedSec: z.number().int().min(0).max(300).optional(),
    /** Plain-language rule for when an argument still counts at the end (shown on the Decide tab). */
    extensionNote: z.string().max(300).optional(),
    sideLabels: z.tuple([z.string().trim().min(1).max(12), z.string().trim().min(1).max(12)]),
    teamSize: z.number().int().min(1).max(5),
    /** coinFlip: order decided per round. fixed: side 0 always speaks first. */
    order: z.enum(['coinFlip', 'fixed']),
    prepSec: z.number().int().min(0).max(1800),
    graceSec: z.number().int().min(0).max(120),
    points: z.object({ min: z.number().min(0), max: z.number().max(100), step: z.number().positive() }),
    /** An argument must appear in these roles of its own side's speeches to count at the end. */
    extensionRoles: z.array(SegmentRole),
    segments: z.array(SegmentSchema).min(1).max(40),
    sheets: z.array(SheetSchema).min(1).max(10),
  })
  // Cross-field rules. Each adds its own issue so every problem is reported together.
  .superRefine((f, ctx) => {
    const ids = new Map<string, number>();
    f.segments.forEach((s, i) => {
      if (ids.has(s.id)) {
        ctx.addIssue({ code: 'custom', path: ['segments', i, 'id'], message: `Short id "${s.id}" is used twice (rows ${ids.get(s.id)! + 1} and ${i + 1}).` });
      } else ids.set(s.id, i);
      s.speakers.forEach((n, j) => {
        if (n > f.teamSize) {
          ctx.addIssue({ code: 'custom', path: ['segments', i, 'speakers', j], message: `Speaker ${n} doesn't exist on a ${f.teamSize}-person team (row ${i + 1}).` });
        }
      });
      if (s.kind === 'questioning' && s.role !== 'questioning') {
        ctx.addIssue({ code: 'custom', path: ['segments', i, 'role'], message: `Row ${i + 1} is a questioning period, so its role should be "questioning".` });
      }
    });
    f.sheets.forEach((sh, i) => {
      sh.segmentIds.forEach((sid, j) => {
        if (!ids.has(sid)) ctx.addIssue({ code: 'custom', path: ['sheets', i, 'segmentIds', j], message: `Sheet "${sh.title}" uses segment "${sid}", which doesn't exist.` });
      });
    });
    if (f.points.min >= f.points.max) {
      ctx.addIssue({ code: 'custom', path: ['points', 'max'], message: 'Highest points must be above lowest points.' });
    }
  });

export type Segment = z.infer<typeof SegmentSchema>;
export type Sheet = z.infer<typeof SheetSchema>;
export type Format = z.infer<typeof FormatSchema>;
export type Slot = 'first' | 'second';
export type SideIndex = 0 | 1;

export const formatLabels = {
  'segments.*.id': 'Short id',
  'segments.*.label': 'Segment name',
  'segments.*.short': 'Abbreviation',
  'segments.*.durationSec': 'Time',
  'sheets.*.title': 'Sheet title',
  prepSec: 'Prep time',
  graceSec: 'Grace period',
  teamSize: 'Team size',
  sideLabels: 'Side names',
};
